import { css } from '@emotion/core';
import {
  warning, gray4, gray3,
} from 'styles/global_defaults/colors';
import {
  standardSpacing, quarterSpacing, threeQuartersSpacing, halfSpacing,
} from 'styles/global_defaults/scaffolding';

import { NvTooltip } from '../../shared/components/nv-tooltip';

export type RankingBarsDataUnit = {
  total: number,
  actual: number,
  title: string,
  catalogId: string,
};

type RankingBarsProps = {
  data: RankingBarsDataUnit[],
  activeLabelTranslation: (num: string | number) => string,
  hideLogins?: boolean,
};

export const RankingBars = (props: RankingBarsProps) => {
  // Sort the data so that larger items are rendered at the top
  const data = props.data ? props.data.sort((a, b) => b.total - a.total) : [];

  // Maximum width is 100%; can be redefined as a max pixel length if needed
  const maxBarWidth = 100;

  let maxValue = 0;
  props.data.forEach((dataUnit) => {
    maxValue = Math.max(maxValue, dataUnit.total);
  });

  const styles = css`
    display: -ms-grid;
    display: grid;
    grid-template-columns: auto minmax(0px, 510px);
    -ms-grid-columns: 1fr auto minmax(0px, 510px) 1fr;
    grid-auto-rows: auto;
    grid-gap: ${standardSpacing}px;

    .total-label {
      text-align: right;
      align-self: end;
      /* Forces the bottom of the text to align with the fill bar */
      line-height: 25px;

      /* Offset the total to account % logged in text */
      ${!props.hideLogins ? css`
        margin-bottom: 15px;
      ` : ''}

      /** IE specific styles http://browserhacks.com/#hack-a60b03e301a67f76a5a22221c739dc64 */
      @media screen and (min-width:0\0) {
        margin-top: 10px;
        margin-right: 20px;
        margin-bottom: 20px;
      }
    }

    .title-label {
      margin-bottom: ${quarterSpacing}px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .catalogId-label {
      color: ${gray3};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .baseline-row {
      display: flex;
      flex-direction: row;
      align-items: baseline;
    }

    .bar-container {
      position: relative;
      padding-bottom: 8px;
    }

    .bar {
      height: 5px;
      background: ${gray4};
      border-radius: 3px;
      position: absolute;
      top: 0;

      ${!props.hideLogins ? css`
        &.active {
          background: ${warning};
        }
      ` : ''}
    }
  `;

  const getBarWidth = (count: number): number => (count / maxValue) * maxBarWidth;

  const ProgressBar = (barProps: { dataUnit: RankingBarsDataUnit}) => (
    <div className='bar-container'>
      <div className='bar' css={{ width: `${getBarWidth(barProps.dataUnit.total)}%` }} />
      <div className='bar active' css={{ width: `${getBarWidth(barProps.dataUnit.actual)}%` }} />
    </div>
  );

  const barRows = [];

  const makeIE11GridStyle = (dataUnitIndex: number, col: number) => ({
    msGridColumn: `${col + 2}`,
    msGridRow: `${dataUnitIndex + 2}`,
  });

  /* eslint-disable react/no-array-index-key */
  data.forEach((dataUnit, i) => {
    barRows.push(
      <div key={`${i}-left`} className='total-label text-xl font-weight-bold condensed' css={makeIE11GridStyle(i, 0)}>{dataUnit.total}</div>,
    );
    barRows.push(
      <div key={`${i}-right`} className='right-side' css={makeIE11GridStyle(i, 1)}>
        <div className='baseline-row'>
          <NvTooltip
            text={dataUnit.title}
          >
            <div className='title-label text-medium font-weight-bold'>{dataUnit.title}</div>
          </NvTooltip>
          <NvTooltip
            text={dataUnit.catalogId}
          >
            <div className='catalogId-label text-small'>{`  ${dataUnit.catalogId}`}</div>
          </NvTooltip>
        </div>
        <ProgressBar dataUnit={dataUnit} />
        {!props.hideLogins
          ? <div className='text-small text-warning font-weight-bold'>{`${props.activeLabelTranslation(Math.round((dataUnit.actual / dataUnit.total) * 100))}`}</div>
          : '' }
      </div>,
    );
  });

  return (
    <div css={styles}>
      {barRows}
    </div>
  );
};

export default RankingBars;
